import { useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PROJECTS } from "@/data/content";
import { ProjectCard } from "@/components/ProjectCard";
import { ProjectModal } from "@/components/ProjectModal";
import { SectionLabel } from "@/components/SectionLabel";
import { AccentHeading } from "@/components/AccentHeading";
import { Reveal } from "@/components/Reveal";
import { PillButton } from "@/components/PillButton";
import { openWhatsApp } from "@/lib/whatsapp";

const ArrowBtn = ({ dir, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    aria-label={dir < 0 ? "Previous" : "Next"}
    className="h-11 w-11 grid place-items-center rounded-full border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.04)] text-[var(--text-primary)] transition-colors hover:border-[var(--border-hover)] hover:bg-[rgba(255,245,230,0.08)] focus-ring"
  >
    {dir < 0 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
  </button>
);

export const FeaturedProjects = () => {
  const ref = useRef(null);
  const [activeProject, setActiveProject] = useState(null);

  const scrollByDir = (dir) => {
    const el = ref.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.85, 440);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section id="properties" data-testid="featured-properties" className="relative overflow-x-clip bg-[var(--bg-primary)] py-16 md:py-24 lg:py-28">
      <div className="container-pad">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal><SectionLabel>OUR PROJECTS</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <AccentHeading as="h2" text="Featured *Properties*" className="mt-4 block text-4xl leading-[1.02] md:text-5xl lg:text-6xl" />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 max-w-[46ch] text-[var(--text-secondary)]">We blend design, craftsmanship, and trust to create homes you&rsquo;ll love.</p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-3">
              <PillButton variant="outline" onClick={() => openWhatsApp("Hello, I would like to view all available Godavari Builders projects.")}>View All</PillButton>
              <div className="hidden items-center gap-2 md:flex">
                <ArrowBtn dir={-1} onClick={() => scrollByDir(-1)} testId="featured-properties-prev-button" />
                <ArrowBtn dir={1} onClick={() => scrollByDir(1)} testId="featured-properties-next-button" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div
        ref={ref}
        data-testid="featured-properties-scroll"
        className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 md:gap-6 md:px-8 xl:px-14"
        style={{ scrollPaddingLeft: "20px" }}
      >
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.id} project={p} index={i} onOpen={setActiveProject} />
        ))}
        <div className="w-2 shrink-0" aria-hidden="true" />
      </div>

      <div className="container-pad mt-6 flex md:hidden">
        <span className="text-xs text-[var(--text-muted)]">Swipe to explore &rarr;</span>
      </div>

      <ProjectModal project={activeProject} onClose={() => setActiveProject(null)} />
    </section>
  );
};
